import React from 'react';
import { Modal, Table } from 'antd';
import { useTranslation } from 'react-i18next';

interface JsonArrayTableViewModalProps {
  isVisible: boolean;
  handleClose: () => void;
  data: any[];
  fields?: any[];
  title?: string;
}

const renderCellValue = (value: any, t: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? t('Yes') : t('No');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const JsonArrayTableViewModal: React.FC<JsonArrayTableViewModalProps> = (props) => {
  const {
    isVisible,
    handleClose,
    data,
    fields,
    title,
  } = props;
  const { t } = useTranslation();

  const rows = Array.isArray(data) ? data : [];
  const isObjectArray = rows.some(
    (row) => row !== null && typeof row === 'object' && !Array.isArray(row),
  );

  const getColumns = () => {
    if (Array.isArray(fields) && fields.length) {
      return fields.map((subField: any, index: number) => {
        const dataKey = subField.columnName || subField.id || `column_${index}`;
        return {
          dataIndex: index,
          title: t(subField.prettyName || dataKey),
          key: dataKey,
          width: '200px',
          render: (_: any, record: any) => renderCellValue(record?.[dataKey], t),
        };
      });
    }
    // Fallback: derive columns from the keys present in the data.
    if (!isObjectArray) {
      return [{
        title: t('Value'),
        dataIndex: 'value',
        key: 'value',
        width: '200px',
        render: (value: any) => renderCellValue(value, t),
      }];
    }
    const keySet = new Set<string>();
    rows.forEach((row) => {
      if (row !== null && typeof row === 'object') {
        Object.keys(row).forEach((key) => keySet.add(key));
      }
    });
    return Array.from(keySet).map((key) => ({
      title: t(key),
      dataIndex: key,
      key,
      width: '200px',
      render: (value: any) => renderCellValue(value, t),
    }));
  };

  const tableData = (isObjectArray ? rows : rows.map((value) => ({ value })))
    .map((row, index) => ({ ...row, tableRowId: index }));

  return (
    <Modal
      title={title || t('Details')}
      width={800}
      visible={isVisible}
      footer={null}
      onCancel={handleClose}
      destroyOnClose
    >
      <Table
        columns={getColumns()}
        dataSource={tableData}
        rowKey="tableRowId"
        pagination={false}
        scroll={{ x: 'max-content', y: 300 }}
      />
    </Modal>
  );
};

export default JsonArrayTableViewModal;
